import { createSlice } from "@reduxjs/toolkit";

const STEP5_INITIAL = {
    cruiseRange: null,
    opportunities: [],
    royalCube: "",
    popUpLeaflet: "",
    cruiseBrands: [],
    landBrands: [],
    marketSentiment: null,
    supportText: "",
};

const STEP6_INITIAL = {
    meetingSummary: "",
    tasks: [],
    newTask: "",
    focusAreas: [],
    targetValues: {},
    savedTargets: {},
};

// The step 5/6 answers belong to one visit: `storeVisitId` records which. It used to be a single
// unkeyed blob, persisted across restarts and only cleared by leaveVisit — any other way out of a
// visit left it behind, and the next store's first visit (the backend has no answers to prefill
// with) opened showing the previous store's answers (Teamwork 41849163). Reads for another visit
// now get the empty defaults, and the first write for a new visit drops the old one.
const emptyVisitFormData = (storeVisitId = null) => ({
    storeVisitId,
    step5: { ...STEP5_INITIAL },
    step6: { ...STEP6_INITIAL },
});

const initialState = {
    data: {},
    loading: false,
    error: null,
    selectedCountry: null,
    selectedBrand: null,
    currentVisitId: null,
    visitProgress: {},
    visitFormData: emptyVisitFormData(),
};

// Exported for app/localStorage.js: persisted state written by a previous
// bundle version is merged onto these defaults at boot, so every branch the
// current code dereferences is guaranteed to exist.
export const royalMomentumInitialState = initialState;

export const royalMomentumSlice = createSlice({
    name: "royalMomentum",
    initialState,
    reducers: {
        setRoyalMomentumData: (state, action) => {
            state.data = action.payload;
        },
        setRoyalMomentumLoading: (state, action) => {
            state.loading = action.payload;
        },
        setRoyalMomentumError: (state, action) => {
            state.error = action.payload;
        },
        clearRoyalMomentumData: (state) => {
            state.data = {};
            state.loading = false;
            state.error = null;
        },
        setRoyalMomentumSelectedCountry: (state, action) => {
            state.selectedCountry = action.payload;
        },
        setRoyalMomentumSelectedBrand: (state, action) => {
            state.selectedBrand = action.payload;
        },
        startVisit: (state, action) => {
            const visitId = action.payload || Date.now().toString();
            state.currentVisitId = visitId;
            if (!state.visitProgress) state.visitProgress = {};
            if (!state.visitProgress[visitId]) {
                state.visitProgress[visitId] = 1;
            }
        },
        setVisitHighestStep: (state, action) => {
            const id = state.currentVisitId;
            if (!state.visitProgress) state.visitProgress = {};
            if (id && action.payload > (state.visitProgress[id] || 0)) {
                state.visitProgress[id] = action.payload;
            }
        },
        setStepFormData: (state, action) => {
            const { step, data } = action.payload;
            const storeVisitId = action.payload.storeVisitId ? String(action.payload.storeVisitId) : null;
            if (!state.visitFormData || !storeVisitId || state.visitFormData.storeVisitId !== storeVisitId) {
                state.visitFormData = emptyVisitFormData(storeVisitId);
            }
            if (state.visitFormData[step]) {
                state.visitFormData[step] = { ...state.visitFormData[step], ...data };
            }
        },
        clearVisitFormData: (state) => {
            state.visitFormData = emptyVisitFormData();
        },
        leaveVisit: (state) => {
            if (!state.visitProgress) state.visitProgress = {};
            if (state.currentVisitId) {
                delete state.visitProgress[state.currentVisitId];
                state.currentVisitId = null;
            }
            state.visitFormData = emptyVisitFormData();
        },
    },
    extraReducers: () => {},
});

export const {
    setRoyalMomentumData,
    setRoyalMomentumLoading,
    setRoyalMomentumError,
    clearRoyalMomentumData,
    setRoyalMomentumSelectedCountry,
    setRoyalMomentumSelectedBrand,
    startVisit,
    setVisitHighestStep,
    leaveVisit,
    setStepFormData,
    clearVisitFormData,
} = royalMomentumSlice.actions;

export const selectRoyalMomentumData = (state) => state.royalMomentum.data;
export const selectRoyalMomentumLoading = (state) => state.royalMomentum.loading;
export const selectRoyalMomentumError = (state) => state.royalMomentum.error;
export const selectRoyalMomentumSelectedCountry = (state) => state.royalMomentum.selectedCountry;
export const selectRoyalMomentumSelectedBrand = (state) => state.royalMomentum.selectedBrand;
export const selectCurrentVisitId = (state) => state.royalMomentum.currentVisitId;
export const selectVisitHighestStep = (state) => {
    const id = state.royalMomentum.currentVisitId;
    const progress = state.royalMomentum.visitProgress || {};
    return id ? (progress[id] || 1) : 1;
};
// Pass the visit being shown; answers saved for any other visit (or with no visit id) are not returned.
const formDataForVisit = (state, storeVisitId) => {
    const form = state.royalMomentum.visitFormData;
    return storeVisitId && form?.storeVisitId === String(storeVisitId) ? form : null;
};
export const selectStep5FormData = (state, storeVisitId) => formDataForVisit(state, storeVisitId)?.step5 ?? STEP5_INITIAL;
export const selectStep6FormData = (state, storeVisitId) => formDataForVisit(state, storeVisitId)?.step6 ?? STEP6_INITIAL;

export default royalMomentumSlice.reducer;
